import {Inject, Injectable, Injector, LOCALE_ID} from '@angular/core';
import {BehaviorSubject, firstValueFrom, from, Observable, of, switchMap} from 'rxjs';
import {catchError, filter} from 'rxjs/operators';
import {BaseService, ClientConfigurationService} from '@twpub/core/services';
import {BrowserStorageUtils} from '@twpub/shared/utils';
import {ActivatedRoute, Router} from '@angular/router';
import {KeycloakService} from 'keycloak-angular';
import {environment} from '../../../environments/environment';
import {KeycloakConfig} from 'keycloak-js';
import {getDecodedJwt} from '@twpub/shared/utils/jwt-util';

export interface SecurityConfig {
  searchUserGroup?: string;
  adminUserGroups?: string[];
  superAdminGroup?: string;
  url?: string;
  externalLogout: boolean;
  keycloakEnabled: boolean;
  anonymousEnabled: boolean;
}

export interface UserInfo {
  sub: string;
  name: string;
  email: string;
  preferred_username: string;
  groups: string[];
  abacAuthenticated?: boolean;
}

export type AuthMethodType = 'external' | 'keycloak'

const {
  EXT_TOKEN_KEY,
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
  AUTH_METHOD_KEY,
  KEYCLOAK_ADMIN_MODE_KEY
} = BrowserStorageUtils;

@Injectable({providedIn: 'root'})
export class SecurityConfigService extends BaseService {
  private cachedConfig?: SecurityConfig;
  private userSubject = new BehaviorSubject<UserInfo | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private authInitializedSubject = new BehaviorSubject<boolean>(false);
  readonly authInitialized$ = this.authInitializedSubject.asObservable();
  private keycloakAdminMode = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private silentRefreshInProgress = false;
  private localePrefix: string;

  private router: Router;
  private readonly route: ActivatedRoute;
  private kcService: KeycloakService;
  private configService: ClientConfigurationService;

  constructor(injector: Injector, @Inject(LOCALE_ID) localeId: string) {
    super(injector);
    this.router = injector.get(Router);
    this.route = injector.get(ActivatedRoute);
    this.kcService = injector.get(KeycloakService);
    this.configService = injector.get(ClientConfigurationService);
    this.localePrefix = environment.production ? `/${localeId.split('-')[0]}` : '';

    // Load Keycloak admin mode from localStorage
    this.keycloakAdminMode = getLocalStorage(KEYCLOAK_ADMIN_MODE_KEY) === 'true';
  }

  public async initializeAuthentication(): Promise<void> {
    try {
      this.cachedConfig = await firstValueFrom(this.http.get<SecurityConfig>(this.url('security/config')));

      // Check if user is navigating to /auth/keycloak route - if so, prioritize Keycloak initialization
      // Check both pathname and hash to handle different routing modes
      const currentPath = window.location.pathname + window.location.hash;
      const isKeycloakAuthRoute = currentPath.includes('/auth/keycloak');

      // If anonymous access is enabled and user is not navigating to admin/keycloak routes,
      // skip Keycloak initialization entirely to avoid unnecessary redirects
      const isAdminRoute = currentPath.includes('/admin') || currentPath.includes('/superadmin');
      if (this.cachedConfig.anonymousEnabled && !isKeycloakAuthRoute && !isAdminRoute && !this.keycloakAdminMode) {
        this.logger.debug('SecurityConfigService.initializeAuthentication: anonymous access enabled, skipping Keycloak init');
        return;
      }

      // If user previously authenticated via Keycloak (keycloakAdminMode), restore that session
      // This handles the case where external auth is configured but user used /auth/keycloak
      if (this.keycloakAdminMode && this.cachedConfig.keycloakEnabled) {
        this.logger.debug('SecurityConfigService.initializeAuthentication: restoring keycloak session (keycloakAdminMode)');
        await firstValueFrom(this.initializeKeycloak());
        return;
      }

      // If user is on /auth/keycloak route, initialize Keycloak immediately instead of external auth
      if (isKeycloakAuthRoute && this.cachedConfig.keycloakEnabled) {
        this.logger.debug('SecurityConfigService.initializeAuthentication: on keycloak auth route, initializing Keycloak');
        await firstValueFrom(this.initializeKeycloak());
        return;
      }

      if (this.isExternalAuth()) {
        this.logger.trace('SecurityConfigService.initializeAuthentication: external', {config: JSON.stringify(this.cachedConfig)});
        this.initExternalAuth();
      } else {
        this.logger.trace('SecurityConfigService.initializeAuthentication: keycloak', {config: this.cachedConfig});
        await firstValueFrom(this.initializeKeycloak());
      }
    } finally {
      // Mark authentication initialization as complete regardless of outcome
      this.authInitializedSubject.next(true);
    }
  }

  private initExternalAuth(): void {
    if (!this.checkForUrlToken()) {
      if (!this.checkForStoredToken()) {
        this.logger.trace('SecurityConfigService.initExternalAuth: no token found');
      }
    }
  }

  private checkForStoredToken = () => {
    const storedToken = this.getToken();
    if (storedToken && this.isUserAuthenticatedExternally()) {
      this.handleExternalAuthToken(storedToken);
      return true;
    }
    return false;
  }

  private checkForUrlToken = () => {
    let found: boolean = false;
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      this.handleExternalAuthToken(token);
      this.storeToken(token, 'external');

      // Clean up the URL by removing the token parameter
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {token: null},
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
      found = true;
    }
    return found;
  }

  initializeKeycloak(): Observable<boolean> {
    return this.configService.getAuthConfiguration().pipe(
      switchMap(authConfig => {
        const config: KeycloakConfig = {
          url: environment.keycloak?.url,
          realm: environment.keycloak?.realm || authConfig.realm,
          clientId: environment.keycloak?.clientId || authConfig.clientId
        };
        const initOptions: Keycloak.KeycloakInitOptions = {
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + this.localePrefix + '/assets/silent-check-sso.html'
        }
        this.logger.debug('Initializing Keycloak with config:', config);
        return from(this.kcService.init({config, initOptions}).then(async () => {
          this.logger.debug('Keycloak initialized successfully');
          return await this.checkKeycloakAuthentication();
        }).catch(error => {
          this.logger.error('Keycloak initialization failed:', error);
          return false;
        }));
      }),
      catchError(error => {
        this.logger.error('Error during Keycloak initialization:', error);
        return of(false);
      })
    );
  }

  /**
   * Initialize Keycloak with login-required — used as fallback when the adapter
   * isn't available and login() cannot be called directly.
   */
  async initializeKeycloakWithLogin(redirectUri: string): Promise<void> {
    const authConfig = await firstValueFrom(this.configService.getAuthConfiguration());
    const config: KeycloakConfig = {
      url: environment.keycloak?.url,
      realm: environment.keycloak?.realm || authConfig.realm,
      clientId: environment.keycloak?.clientId || authConfig.clientId
    };
    await this.kcService.init({
      config,
      initOptions: {onLoad: 'login-required', redirectUri}
    });
  }

  private handleExternalAuthToken(token: string): void {
    this.logger.debug('Handling external token');
    try {
      const userInfo = this.decodeJwtToken(token);
      if (userInfo && this.isTokenValid(token)) {
        this.userSubject.next(userInfo);
        this.isAuthenticatedSubject.next(true);
        this.scheduleTokenRefresh(token);
      } else {
        this.logger.debug('Invalid token from external authentication');
        this.handleAuthenticationFailure();
      }
    } catch (error) {
      this.logger.error('Error processing token from external authentication', error);
      this.handleAuthenticationFailure();
    }
  }

  private async checkKeycloakAuthentication(): Promise<boolean> {
    try {
      const isKeycloakAuthenticated = await this.kcService.isLoggedIn();
      if (isKeycloakAuthenticated) {
        const keycloakProfile = await this.kcService.loadUserProfile();

        // Convert Keycloak user info to our UserInfo format
        const userInfo: UserInfo = {
          sub: keycloakProfile.id || '',
          name: (keycloakProfile.firstName || '') + ' ' + (keycloakProfile.lastName || ''),
          email: keycloakProfile.email || '',
          preferred_username: keycloakProfile.username || '',
          groups: this.extractKeycloakRoles()
        };
        this.userSubject.next(userInfo);
        this.isAuthenticatedSubject.next(true);
        setLocalStorage(AUTH_METHOD_KEY, 'keycloak');
        // this.storeToken(token, 'keycloak');
        return true;
      } else {
        // Not authenticated via either method
        this.isAuthenticatedSubject.next(false);
      }
    } catch (error) {
      this.logger.error('Error checking Keycloak authentication:', error);
      this.isAuthenticatedSubject.next(false);
    }
    return false;
  }

  private decodeJwtToken(token: string): UserInfo | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        this.logger.error('Invalid JWT format');
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));

      return {
        sub: payload.sub || '',
        name: payload.name || '',
        email: payload.email || '',
        preferred_username: payload.preferred_username || '',
        groups: payload.groups || [],
        abacAuthenticated: payload.abac_authenticated || false
      };
    } catch (error) {
      this.logger.error('Error decoding JWT token:', error);
      return null;
    }
  }

  private isTokenValid(token: string): boolean {
    try {
      const payload = this.getPayload(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return payload?.exp && payload?.exp > currentTime;
    } catch (error) {
      this.logger.error('Error validating token:', error);
      return false;
    }
  }

  private getPayload = (token?: string) => {
    const parts = token?.split('.');
    return parts ? JSON.parse(atob(parts[1])) : undefined;
  }

  private extractKeycloakRoles(): string[] {
    try {
      const token = this.kcService.getKeycloakInstance().token;
      const payload = this.getPayload(token);
      if (!payload) return [];

      // Extract roles and groups from various places in the Keycloak token
      const roles: string[] = [];

      // Groups claim (configured via Keycloak client mapper)
      if (payload.groups && Array.isArray(payload.groups)) {
        // Normalize groups: remove leading slash
        roles.push(...payload.groups.map((g: string) => g?.startsWith('/') ? g.slice(1) : g));
      }

      // Realm roles
      if (payload.realm_access && payload.realm_access.roles) {
        roles.push(...payload.realm_access.roles);
      }

      // Client roles
      if (payload.resource_access) {
        Object.values(payload.resource_access).forEach((clientAccess: any) => {
          if (clientAccess.roles) {
            roles.push(...clientAccess.roles);
          }
        });
      }

      return roles;
    } catch (error) {
      this.logger.error('Error extracting Keycloak roles:', error);
      return [];
    }
  }

  private handleAuthenticationFailure(): void {
    this.storeToken();
    this.isAuthenticatedSubject.next(false);
  }

  public async login(): Promise<void> {
    // If in keycloakAdminMode, always use Keycloak login even if external auth is configured
    if (this.keycloakAdminMode) {
      await this.kcService.login();
      return;
    }

    if (this.isExternalAuth()) {
      let url = this.getConfig().url!;
      url += '?returnUrl=' + encodeURIComponent(window.location.origin);
      window.location.href = url;
    } else {
      await this.kcService.login();
    }
  }

  public async externalLogout(): Promise<void> {
    this.logger.trace('SecurityConfigService.externalLogout:')
    if (this.isExternalAuth() && this.isExternalLogout()) {
      await this.logoutUser()
      const logoutUrl = `${this.getConfig().url!}/logout`;
      this.logger.trace('SecurityConfigService.externalLogout redirect to: ', logoutUrl)
      window.location.href = logoutUrl;
    }
  }

  public getUser(): UserInfo | null {
    return this.userSubject.value;
  }

  public isUserAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Ensures Keycloak has performed a session check. When anonymous access is enabled,
   * {@link initializeAuthentication} skips Keycloak init on non-admin routes, so a
   * logged-in user's session isn't detected on e.g. the search page until they hit an
   * admin route. UI that is gated on {@link isUserAuthenticated} can call this first.
   * No-op if the user is already authenticated, external auth is in use, or Keycloak
   * has already been initialized.
   */
  async ensureKeycloakSessionChecked(): Promise<void> {
    // Wait for startup auth init so the security config (and any already-detected session) is available.
    await firstValueFrom(this.authInitialized$.pipe(filter(v => v)));
    if (this.isUserAuthenticated()) {
      return;
    }
    if (this.isExternalAuth() && !this.keycloakAdminMode) {
      return;
    }
    // The Keycloak adapter exposes `authenticated` (true/false) only once init() has run.
    // (isLoggedIn() can't be used to detect this — it returns false rather than throwing when uninitialized.)
    let keycloakInitialized: boolean;
    try {
      keycloakInitialized = this.kcService.getKeycloakInstance()?.authenticated !== undefined;
    } catch {
      keycloakInitialized = false;
    }
    if (keycloakInitialized) {
      return;
    }
    this.logger.debug('SecurityConfigService.ensureKeycloakSessionChecked: running Keycloak check-sso');
    await firstValueFrom(this.initializeKeycloak());
  }

  isAdmin(): boolean {
    // Check if user is in Keycloak admin mode (for /auth/keycloak page)
    if (this.keycloakAdminMode && this.isUserAuthenticated()) {
      return true;
    }

    const cfg = this.getConfig();
    const adminGroups = cfg.adminUserGroups ?? [];

    // If no admin groups are configured, no one can be admin via groups
    if (adminGroups.length === 0) {
      return false;
    }

    // Normalize group names: remove leading slash for comparison
    const normalizeGroup = (g: string) => g?.startsWith('/') ? g.slice(1) : g;
    const isInAnyAdminGroup = (userGroups: string[]) => {
      const normalizedUserGroups = userGroups.map(normalizeGroup);
      return adminGroups.some(adminGroup => normalizedUserGroups.includes(normalizeGroup(adminGroup)));
    };

    if (this.isUserAuthenticated()) {
      const user = this.getUser();
      return !!user && isInAnyAdminGroup(user.groups);
    }

    // Fallback to JWT token checking (try access token first, then external token)
    const tokenParsed: any = getDecodedJwt() || getDecodedJwt(BrowserStorageUtils.EXT_TOKEN_KEY);
    // Collect groups from both 'groups' and 'authorities' claims
    const groups: string[] = (tokenParsed?.groups as string[] ?? []).map(normalizeGroup);
    const authorities: string[] = (tokenParsed?.authorities as string[] ?? []).map(a => {
      if (a.startsWith('GROUP_')) return a.slice(6);
      if (a.startsWith('/')) return a.slice(1);
      return a;
    });
    // Check for any admin group in either claim
    const allUserGroups = [...groups, ...authorities];
    return adminGroups.some(adminGroup => allUserGroups.includes(normalizeGroup(adminGroup)));
  }

  isSuperAdmin(): boolean {
    const cfg = this.getConfig();
    const superAdminGroup = cfg.superAdminGroup;
    if (!superAdminGroup) return false;

    const normalizeGroup = (g: string) => g?.startsWith('/') ? g.slice(1) : g;
    const normalizedSuperAdmin = normalizeGroup(superAdminGroup);

    const checkGroups = (userGroups: string[]) =>
      userGroups.map(normalizeGroup).includes(normalizedSuperAdmin);

    if (this.isUserAuthenticated()) {
      const user = this.getUser();
      return !!user && checkGroups(user.groups);
    }

    // Fallback to JWT token checking (try access token first, then external token)
    const tokenParsed: any = getDecodedJwt() || getDecodedJwt(BrowserStorageUtils.EXT_TOKEN_KEY);
    const groups: string[] = (tokenParsed?.groups as string[] ?? []).map(normalizeGroup);
    const authorities: string[] = (tokenParsed?.authorities as string[] ?? []).map(a => {
      if (a.startsWith('GROUP_')) return a.slice(6);
      if (a.startsWith('/')) return a.slice(1);
      return a;
    });
    return [...groups, ...authorities].includes(normalizedSuperAdmin);
  }

  public setKeycloakAdminMode(enabled: boolean): void {
    this.keycloakAdminMode = enabled;
    if (enabled) {
      setLocalStorage(KEYCLOAK_ADMIN_MODE_KEY, 'true');
    } else {
      removeLocalStorage(KEYCLOAK_ADMIN_MODE_KEY);
    }
  }

  public isKeycloakAdminMode(): boolean {
    return this.keycloakAdminMode;
  }

  getConfig(): SecurityConfig {
    if (this.cachedConfig) return this.cachedConfig;
    this.logger.warn('SecurityConfig not initialized');
    return {
      externalLogout: false,
      keycloakEnabled: false,
      anonymousEnabled: false,
      adminUserGroups: ['dummy-admins'],
      searchUserGroup: 'dummy-users'
    };
  }

  async logoutUser() {
    const authMethod = getLocalStorage(AUTH_METHOD_KEY);
    this.userSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.storeToken();

    // Clear Keycloak admin mode on logout
    this.setKeycloakAdminMode(false);

    if (authMethod === 'keycloak') {
      this.logger.trace('SecurityConfigService.logoutUser logging out from keycloak');
      // Keycloak logout will redirect the browser, so we set the redirect URI to home page
      await this.kcService.logout(window.location.origin + '/');
    } else {
      await this.router.navigate(['/']);
    }
  }

  storeToken = (token?: string, authMethod?: AuthMethodType) => {
    if (token) {
      setLocalStorage(EXT_TOKEN_KEY, token);
    } else {
      removeLocalStorage(EXT_TOKEN_KEY);
    }
    if (authMethod) {
      setLocalStorage(AUTH_METHOD_KEY, authMethod);
    } else {
      removeLocalStorage(AUTH_METHOD_KEY);
    }
  }

  getToken = () => getLocalStorage(EXT_TOKEN_KEY);
  isUserAuthenticatedExternally = () => getLocalStorage(AUTH_METHOD_KEY) === 'external';
  isExternalAuth = () => !!this.getConfig().url;
  isExternalLogout = () => this.getConfig().externalLogout;

  private scheduleTokenRefresh(token: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    try {
      const payload = this.getPayload(token);
      if (!payload?.exp || !payload?.iat) return;
      const lifetime = payload.exp - payload.iat;
      const now = Math.floor(Date.now() / 1000);
      const refreshAt = payload.iat + Math.floor(lifetime * 0.8);
      const delayMs = Math.max((refreshAt - now) * 1000, 0);
      this.logger.debug(`Token refresh scheduled in ${Math.round(delayMs / 1000)}s (lifetime=${lifetime}s)`);
      this.refreshTimer = setTimeout(() => this.silentRefresh(), delayMs);
    } catch (error) {
      this.logger.error('Error scheduling token refresh', error);
    }
  }

  silentRefresh(): Promise<string | null> {
    if (this.silentRefreshInProgress) {
      return this._silentRefreshPromise!;
    }
    this.silentRefreshInProgress = true;
    this._silentRefreshPromise = this.doSilentRefresh().finally(() => {
      this.silentRefreshInProgress = false;
      this._silentRefreshPromise = null;
    });
    return this._silentRefreshPromise;
  }

  private _silentRefreshPromise: Promise<string | null> | null = null;

  private doSilentRefresh(): Promise<string | null> {
    return new Promise((resolve) => {
      const pussUrl = this.getConfig().url;
      if (!pussUrl) {
        this.logger.debug('No external auth URL configured, skipping silent refresh');
        resolve(null);
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';

      const silentRefreshUrl = window.location.origin + this.localePrefix + '/assets/silent-refresh.html';
      let loginUrl = pussUrl + '/login?prompt=none'
        + '&returnUrl=' + encodeURIComponent(silentRefreshUrl);

      // Pass login_hint to help Entra identify the user for silent auth
      const user = this.userSubject.getValue();
      if (user?.preferred_username) {
        loginUrl += '&login_hint=' + encodeURIComponent(user.preferred_username);
      }

      const timeoutMs = 15000;
      let resolved = false;

      const cleanup = () => {
        window.removeEventListener('message', messageHandler);
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };

      const messageHandler = (event: MessageEvent) => {
        this.logger.debug('Received postMessage from origin: ' + event.origin + ', expected: ' + window.location.origin);
        if (event.origin !== window.location.origin) return;
        if (!event.data || typeof event.data !== 'object') return;
        this.logger.debug('postMessage data type: ' + event.data.type);
        if (event.data.type === 'silent-refresh-success' && event.data.token) {
          resolved = true;
          clearTimeout(timer);
          cleanup();
          this.handleRefreshResult(event.data.token);
          resolve(event.data.token);
        } else if (event.data.type === 'silent-refresh-error') {
          resolved = true;
          clearTimeout(timer);
          cleanup();
          this.logger.debug('Silent refresh failed: ' + event.data.error);
          resolve(null);
        }
      };

      window.addEventListener('message', messageHandler);

      this.logger.debug('Silent refresh iframe URL: ' + loginUrl);

      const timer = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          this.logger.debug('Silent refresh timed out after ' + timeoutMs + 'ms');
          resolve(null);
        }
      }, timeoutMs);

      iframe.src = loginUrl;
      document.body.appendChild(iframe);
    });
  }

  private handleRefreshResult(token: string): void {
    this.logger.debug('Silent refresh succeeded, updating token');
    this.storeToken(token, 'external');
    this.handleExternalAuthToken(token);
  }
}
